import React, { useMemo } from 'react';
import { StructuredData } from 'components/SEO';
import { PublicSetCardAll } from 'store/services/sets/publicSetsApi';
import { buildCardUrl } from 'utils/cardUrlUtils';
import { formatRarity, parseCollectorNumber } from './setCardsShared';

interface SetItemListSchemaProps {
  cards: PublicSetCardAll[];
  setCode: string;
  setName: string;
  /** How many leading cards (by collector number) carry a full Product item. */
  detailLimit?: number;
}

// Ruling 2026-09-02: full Product detail for the first `detailLimit` cards by
// collector number (deterministic, matching the list view's order); lightweight
// name+url ListItems for the remainder so the ItemList still enumerates the
// full set. One code path - no set-size branching. Never emit null or empty
// fields, and never an offers block when usd is null (no fabricated prices).
export const SetItemListSchema: React.FC<SetItemListSchemaProps> = ({
  cards,
  setCode,
  setName,
  detailLimit = 50,
}) => {
  const data = useMemo(() => {
    const ordered = [...cards].sort((a, b) => {
      const aNum = parseCollectorNumber(a.collectorNumber);
      const bNum = parseCollectorNumber(b.collectorNumber);
      if (aNum.numeric !== bNum.numeric) return aNum.numeric - bNum.numeric;
      return aNum.suffix.localeCompare(bNum.suffix);
    });

    const items = ordered.map((c, i) => {
      const url = `https://trademagic.gg${buildCardUrl({
        name: c.name,
        setCode,
        collectorNumber: c.collectorNumber,
        scryfallID: c.scryfallID,
      })}`;

      if (i >= detailLimit) {
        return {
          '@type': 'ListItem',
          position: i + 1,
          url,
          name: c.name,
        };
      }

      const additionalProperty = [
        c.manaCost ? { '@type': 'PropertyValue', name: 'Mana Cost', value: c.manaCost } : null,
        c.typeLine ? { '@type': 'PropertyValue', name: 'Type Line', value: c.typeLine } : null,
        c.rarity ? { '@type': 'PropertyValue', name: 'Rarity', value: formatRarity(c.rarity) } : null,
        c.power ? { '@type': 'PropertyValue', name: 'Power', value: c.power } : null,
        c.toughness ? { '@type': 'PropertyValue', name: 'Toughness', value: c.toughness } : null,
        c.collectorNumber
          ? { '@type': 'PropertyValue', name: 'Collector Number', value: c.collectorNumber }
          : null,
        c.flavorText ? { '@type': 'PropertyValue', name: 'Flavor Text', value: c.flavorText } : null,
      ].filter((p): p is NonNullable<typeof p> => p !== null);

      const product: Record<string, unknown> = {
        '@type': 'Product',
        name: c.name,
        url,
        sku: c.scryfallID,
      };
      if (c.imageUris?.normal) {
        product.image = c.imageUris.normal;
      }
      if (c.oracleText) {
        product.description = c.oracleText;
      }
      if (c.usd != null) {
        product.offers = {
          '@type': 'Offer',
          price: c.usd,
          priceCurrency: 'USD',
          availability: 'https://schema.org/InStock',
          url,
        };
      }
      if (additionalProperty.length > 0) {
        product.additionalProperty = additionalProperty;
      }

      return {
        '@type': 'ListItem',
        position: i + 1,
        item: product,
      };
    });

    return {
      '@context': 'https://schema.org',
      '@type': 'ItemList',
      name: `Cards in ${setName}`,
      numberOfItems: cards.length,
      itemListOrder: 'https://schema.org/ItemListOrderAscending',
      itemListElement: items,
    };
  }, [cards, setCode, setName, detailLimit]);

  if (cards.length === 0) return null;

  return <StructuredData data={data} />;
};
